import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { Button, type ButtonVariant } from '@clay-run/glaze/button';
import { Popover, PopoverButton, PopoverPanel } from '@clay-run/glaze/popover';
import { Text } from '@clay-run/glaze/text';
import { addErrorToast } from '@clay-run/glaze/toast/toast-queue';

import { useApi } from '../../_shared/hooks';
import { setSequenceInflectionFields } from '../mutations';
import { applySequenceInflectionFieldsToCache } from '../sequence-identity-cache';
import {
  resolveSenderChoice,
  senderChoiceFromSender,
  STATIC_EMAIL_SENDER_OPTION,
  type SenderChoice,
} from '../sender-choice';
import { usedInPlacesText } from '../usage-gate';
import { settingsDraftForReopen } from '../settings-draft-restore';
import { useUsageSidebarStore, type UsageSidebarOrigin } from '../usage-sidebar-store';
import { SenderChoiceFields } from './play-flow/SenderChoiceFields';
import { saveSequenceSettingsWithGate } from './SequenceSenderPopover';

/**
 * playbooks-inflection-wiring (2026-09-17) — the CC / Reply-to control for
 * `inflection` sequences. `playbooks_sequence.default_cc`/`default_reply_to`
 * are identity-row fields, same token-or-email vocabulary as
 * `default_sender` (see `SequenceSenderPopover.tsx`), written together (one
 * popover, one Save) via `PATCH /sequences/:id/inflection`.
 *
 * Structural copy of `SequenceSenderPopover.tsx`. At the Library/playbook
 * DOORWAYS (`playId` undefined) it still writes in place with only the
 * `usedInPlacesText(usageCount)` caption for discoverability (TL;DR item 6
 * of that plan — unchanged).
 *
 * Plan 2026-09-28-006 U4 (rulebook R5, R6) — inside a PLAY it goes through
 * the same usage gate as every other sequence edit
 * (`SequenceSenderPopover.tsx#saveSequenceSettingsWithGate`): fetch usage;
 * only this group uses it → silent (owned: in place; Library: forked for
 * this group, then the copy written); anything else → the Save menu with a
 * `{ field: 'inflection', cc, replyTo }` pending, and this popover closes.
 *
 * Renders TWO independent `SenderChoiceFields` groups (own `SenderChoice`
 * state, own `isClear`/error), one labelled "CC" and one "Reply-to" — same
 * vocabulary and resolution logic as sender, run twice.
 */
export function SequenceInflectionPopover({
  sequenceId,
  currentCc,
  currentReplyTo,
  usageCount,
  variant = 'secondary',
  size = 'xs',
  onSaved,
  playId,
  playbookId,
  usageOrigin,
  sequenceName,
  owningPlayId = null,
  archivedInLibrary = false,
}: {
  sequenceId: string;
  currentCc: string | null;
  currentReplyTo: string | null;
  usageCount: number;
  variant?: ButtonVariant;
  size?: 'xs' | 'sm';
  onSaved?: (fields: { cc: string | null; replyTo: string | null }) => void;
  /**
   * Plan 2026-09-25-002, U7 (Decision 21) — passed from the play flow header
   * (`SequencesStep.tsx`) so this save's `?playId=` lets the server's
   * ownership guard confirm the addressed sequence belongs to this play.
   * `undefined` in the playbook doorway, which sends no `playId`.
   */
  playId?: string;
  /**
   * Plan 2026-09-27-006 U7a — required alongside `playId`: this write runs
   * `ensurePlayOwned` first (Key Technical Decision 3), a no-op when the
   * sequence is already this play's own.
   */
  playbookId?: string;
  /**
   * Plan 2026-09-28-006 U4 — the group this edit came from (the play-flow
   * header's rung origin). Required for the in-play gate; unused at the
   * doorway.
   */
  usageOrigin?: UsageSidebarOrigin;
  /** The library row's name — the Save menu's title. */
  sequenceName?: string;
  /** The library row's `owningPlayId` (this play's id for a copy). */
  owningPlayId?: string | null;
  /** [R18] The library row is a Library piece archived while this play still uses it. */
  archivedInLibrary?: boolean;
}) {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [ccChoice, setCcChoice] = useState<SenderChoice>(() => senderChoiceFromSender(currentCc));
  const [replyToChoice, setReplyToChoice] = useState<SenderChoice>(() => senderChoiceFromSender(currentReplyTo));
  const [showError, setShowError] = useState(false);
  const [saving, setSaving] = useState(false);

  const ccIsClear = ccChoice.option === STATIC_EMAIL_SENDER_OPTION && ccChoice.email.trim() === '';
  const ccResolved = resolveSenderChoice(ccChoice);
  const ccError = ccIsClear || ccResolved.ok ? null : ccResolved.error;

  const replyToIsClear = replyToChoice.option === STATIC_EMAIL_SENDER_OPTION && replyToChoice.email.trim() === '';
  const replyToResolved = resolveSenderChoice(replyToChoice);
  const replyToError = replyToIsClear || replyToResolved.ok ? null : replyToResolved.error;

  function handleOpenChange(nextOpen: boolean) {
    if (nextOpen) {
      // Re-seed from the stored values every time the popover opens, so a
      // cancelled edit never leaks into the next one — unless a Save panel
      // for THIS group and THIS field is still pending, in which case the
      // rep's own unsaved values are what they should see (plan
      // 2026-09-29-001 U2, R14; see `settings-draft-restore.ts`).
      const pendingDraft = settingsDraftForReopen(
        useUsageSidebarStore.getState().pending,
        usageOrigin,
        'inflection',
      );
      const restored = pendingDraft?.field === 'inflection' ? pendingDraft : null;
      setCcChoice(senderChoiceFromSender(restored === null ? currentCc : restored.cc));
      setReplyToChoice(senderChoiceFromSender(restored === null ? currentReplyTo : restored.replyTo));
      setShowError(false);
    }
    setOpen(nextOpen);
  }

  function handleSave() {
    if (ccError !== null || replyToError !== null) {
      setShowError(true);
      return;
    }
    const cc = ccIsClear ? null : ccResolved.ok ? ccResolved.sender : null;
    const replyTo = replyToIsClear ? null : replyToResolved.ok ? replyToResolved.sender : null;
    setSaving(true);
    // `group` and `playId` are either both present (in play) or both absent
    // (outside one) — matching `SequenceSettingsWriteOptions`'s union exactly.
    const write = (id: string, group?: { rungId: string; confirmedRungIds?: string[] }) =>
      group === undefined
        ? setSequenceInflectionFields(apiFetch, id, { cc, replyTo }, { playId: undefined })
        : setSequenceInflectionFields(apiFetch, id, { cc, replyTo }, { playId: playId!, group });
    void (
      playId !== undefined && usageOrigin !== undefined
        ? saveSequenceSettingsWithGate(apiFetch, queryClient, {
            sequenceId,
            sequenceName,
            usageOrigin,
            owningPlayId,
            archivedInLibrary,
            draftSettings: { field: 'inflection', cc, replyTo },
            playId,
            playbookId,
            write,
          })
        : write(sequenceId).then((value) => ({ kind: 'written' as const, value }))
    )
      .then((response) => {
        if (response.kind === 'written') {
          applySequenceInflectionFieldsToCache(queryClient, response.value.id, {
            cc: response.value.cc,
            replyTo: response.value.replyTo,
          });
          onSaved?.({ cc: response.value.cc, replyTo: response.value.replyTo });
        }
        // `'menu'` — the Save menu took over; the rep's choice does the write.
        // Plan 2026-09-29-001 U2 (R14, B5) — `'stale'` keeps this popover
        // open on what the rep typed; the Save panel carries the sentence.
        if (response.kind !== 'stale') setOpen(false);
      })
      .catch((err: unknown) => {
        addErrorToast({
          content: err instanceof Error ? err.message : 'Could not update CC / Reply-to — try again.',
        });
      })
      .finally(() => {
        setSaving(false);
      });
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      {/* `data-row-control` — see `SequenceSenderPopover.tsx`'s own note. */}
      <PopoverButton variant={variant} size={size} data-row-control>
        {currentCc === null && currentReplyTo === null ? 'Set CC / Reply-to' : 'Change CC / Reply-to'}
      </PopoverButton>
      <PopoverPanel positioner={{ side: 'bottom', align: 'start' }}>
        <div className="flex w-72 flex-col gap-4 p-3" data-row-control>
          {usedInPlacesText(usageCount) !== null && (
            <Text size="xs" variant="tertiary">
              {usedInPlacesText(usageCount)}
            </Text>
          )}
          <SenderChoiceFields
            label="CC"
            value={ccChoice}
            onChange={setCcChoice}
            error={showError ? ccError : null}
          />
          <SenderChoiceFields
            label="Reply-to"
            value={replyToChoice}
            onChange={setReplyToChoice}
            error={showError ? replyToError : null}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" loading={saving} onClick={handleSave}>
              Save
            </Button>
          </div>
        </div>
      </PopoverPanel>
    </Popover>
  );
}
